import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { AccountDto, AccountsControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toOptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { keyBy } from 'lodash';
import { pipe, switchMap } from 'rxjs';

export const useAccounts = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: inject(NotificationService),
      },
    },
    withState({
      accounts: new Array<AccountDto>(),
      accountsLoaded: false,
    }),
    withProps(() => ({
      _accountsService: inject(AccountsControllerService),
    })),
    withComputed(store => {
      const accountsMap = computed(() => keyBy(store.accounts(), 'id'));

      const accountsOptions = computed(() => store.accounts().map(lp => toOptionItemModel(lp, 'id', 'name')));

      const clientAccounts = computed(() =>
        store
          .accounts()
          .filter(acc => acc.accountType && ['COMPANY', 'INDIVIDUAL', 'EXTERNAL_LP'].includes(acc.accountType)),
      );

      const inventoryAccount = computed(() =>
        store.accounts().find(account => account.name === 'Stillman Digital Inventory'),
      );

      return {
        accountsMap,
        accountsOptions,
        clientAccounts,
        inventoryAccount,
      };
    }),
    withMethods(store => {
      const loadAccounts = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._accountsService.getAccounts({}).pipe(
              tapResponse({
                next: (accounts: AccountDto[]) => patchState(store, { accounts, accountsLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load accounts', error);
                  patchState(store, { accountsLoaded: true });
                },
              }),
            ),
          ),
        ),
      );
      return {
        loadAccounts,
      };
    }),
  );
